import { useEffect, useCallback } from "react";
import { useController, useWatch } from "react-hook-form";
import dayjs from "dayjs";
import { useSearchFormContext } from "./SearchForm";
import { EditableDatePicker } from "../datePicker";

// Date input for react-hook-form with date comparison validation
// compareWith: field name for cross-validation (e.g., "notAfter", "notBefore")
function SearchDateInput({ label, dataLabel, defaultValue, minDate, maxDate, compareWith, comparison }) {
    const { control, errors, setError, clearErrors } = useSearchFormContext();
    const { field } = useController({ name: dataLabel, control, defaultValue: defaultValue || null });

    // Watch the sibling field for cross-field validation (start vs end)
    const siblingValue = useWatch({ control, name: compareWith || "__none__" });

    // Set default value on mount if empty (handles timing with form reset from sessionStorage)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (defaultValue && !field.value) {
                field.onChange(defaultValue);
            }
        }, 0);
        return () => clearTimeout(timer);
    }, [defaultValue]);

    // Cross-field validation: start date must not be after end date (and vice versa)
    useEffect(() => {
        if (!compareWith || !field.value || !siblingValue) return;
        const thisDate = dayjs(field.value);
        const otherDate = dayjs(siblingValue);
        if (!thisDate.isValid() || !otherDate.isValid()) return;

        if (comparison === "notAfter" && thisDate.isAfter(otherDate, "day")) {
            setError(dataLabel, { type: "range", message: "Start Date should not be greater than End Date" });
        } else if (comparison === "notBefore" && thisDate.isBefore(otherDate, "day")) {
            setError(dataLabel, { type: "range", message: "End Date should not be earlier than Start Date" });
        } else {
            // Clear only range errors set by this comparison
            if (errors[dataLabel]?.type === "range") {
                clearErrors(dataLabel);
            }
        }
    }, [field.value, siblingValue, compareWith, comparison]);

    // Called by EditableDatePicker when its internal (format/boundary) validation changes
    const handleValidationChange = useCallback((message) => {
        if (message) {
            setError(dataLabel, { type: "date", message });
        } else if (errors[dataLabel]?.type === "date") {
            clearErrors(dataLabel);
        }
    }, [dataLabel]);

    return (
        <div className="searchForm">
            <label htmlFor={dataLabel}>{label}</label>
            <EditableDatePicker
                value={field.value}
                onChange={(iso) => field.onChange(iso)}
                minDate={minDate}
                maxDate={maxDate}
                error={!!errors[dataLabel]}
                helperText={errors[dataLabel]?.message}
                onValidationChange={handleValidationChange}
                fullWidth
            />
        </div>
    );
}

export default SearchDateInput;
